// The actions on one connector of the office's library, shared by « Connecteurs du bureau »
// (web/ui/myconnectors.js) and Administration → Connecteurs (web/ui/admin/connectors.js):
// CONNECTER / RECONNECTER / REPRENDRE (the connect window, myconnectors/connect.js; RECONNECTER on a
// connected one is asked first: Claude Code drops the current login as a new one starts, and the
// new account replaces the old one for every agent), Vérifier (POST …/check, also from À CONNECTER:
// a server that needs no login), Renommer (PATCH {label}, asked with a prompt) and Retirer
// (DELETE, asked first). The server says who may do them (an administrator any; a member allowed
// to add, the ones he added): the page only offers them where `canManage` says so. A plugin the
// office serves itself (Shopify, Mailgun) never logs in through Claude Code: CONNECTER / MODIFIER
// LA CLÉ / RECONNECTER open its settings window instead (myconnectors/plugin.js) — but a Shopify
// store connected through the Shopify CLI logs in in the connect window like a remote connector
// (its « Réglages » change its store or take a token; RECONNECTER on a connected one is asked
// first: the current connection stays until the new one succeeds); a Google Drive's settings
// window holds its « Se connecter avec Google », folder and storage (myconnectors/drive.js: what
// it changes makes the page read the library again, `onLoginDone`). Each answer goes
// back through `onSaved` / `onRemoved`; `busy` (a request on the way) and `running` (a login left
// finishing when its window closed) say how to show each row.
import { t } from '../../i18n/fr.js';
import { errorText } from '../admin/model.js';
import { childLayers } from '../automations/widgets.js';
import { openConnectWindow } from './connect.js';
import { openPluginSettings } from './plugin.js';
import { connectorOf, isPluginConnector, isShopifyCli, labelTaken, validateLabel, LABEL_MAX, LIBRARY_BASE } from './model.js';
import { GDRIVE_ID } from './drive-model.js';
import './strings.js';

/** Removing runs `claude mcp remove` through the server's queue (adding: `add-json`). */
export const WRITE_TIMEOUT_MS = 60_000;
/** A check starts Claude Code to list the server's tools. */
export const CHECK_TIMEOUT_MS = 90_000;

const enc = encodeURIComponent;

/**
 * Creates the actions.
 * @param {object} ctx app context
 * @param {{parent?: object|null, isOpen?: () => boolean, render?: () => void, list?: () => object[],
 *   onSaved?: (c: object) => void, onRemoved?: (c: object) => void, onLoginDone?: (c: object) => void}} [opts]
 *   parent: the layer whose closing closes the connect windows (else closeAll()); isOpen: false
 *   once the page is gone (answers are then dropped); render: the page shows busy / running again;
 *   onSaved: a connector as the server answered it (a check, a rename, a plugin's settings);
 *   onRemoved: one gone; onLoginDone: a login succeeded (the page reads the list again); list: the
 *   whole library (a new name already used is refused before asking the server)
 * @returns {{busy: Set<string>, running: Set<string>, connect: Function, reconnect: Function, settings: Function,
 *   check: Function, rename: Function, remove: Function, closeAll: () => void}}
 */
export function createLibraryActions(ctx, {
  parent = null, isOpen = () => true, render = () => {}, onSaved = () => {}, onRemoved = () => {}, onLoginDone = () => {}, list = () => [],
} = {}) {
  const busy = new Set();
  const running = new Set();
  const children = childLayers(parent);

  /**
   * Opens the connect window of `c` (closed with the page); a plugin other than a CLI store opens
   * its settings instead.
   * @param {object} c
   * @param {{resume?: boolean}} [opts] resume: follow the login left running (never a new one)
   */
  function connect(c, { resume = false } = {}) {
    if (!isOpen()) return null;
    if (isPluginConnector(c) && !isShopifyCli(c)) return settings(c);
    return login(c, { resume });
  }

  /** The connect window itself (a Shopify store: the Shopify CLI's login, whatever its connection now). */
  function login(c, { resume = false } = {}) {
    return children.add(openConnectWindow(ctx, c, {
      resume,
      onDone: () => { running.delete(c.id); onLoginDone(c); },
      // A login left checking finishes on the server: the row says so until its live event.
      onClose: (job) => {
        if (job?.state === 'checking') running.add(c.id); else running.delete(c.id);
        render();
      },
    }));
  }

  /**
   * The settings window of a plugin (its key, its store, « Se connecter avec Shopify »); a store
   * to connect through the Shopify CLI comes back here for its connect window (`onLogin`), a store
   * still connected by a token included (it keeps it until the CLI's login succeeds).
   */
  function settings(c) {
    if (!isOpen()) return null;
    return children.add(openPluginSettings(ctx, c, {
      onSaved: (saved) => { if (isOpen()) onSaved(saved); },
      // A Google Drive's folder or storage changed: the list is read again (its details, its badge).
      onChanged: () => { if (isOpen()) onLoginDone(c); },
      onLogin: (saved) => {
        if (!isOpen()) return;
        onSaved(saved);
        login(saved);
      },
    }));
  }

  /** RECONNECTER on a connected connector: asked first (the current login is dropped as a new one starts). */
  async function reconnect(c) {
    if (busy.has(c.id)) return;
    const ok = await ctx.overlay.confirm({
      title: t('pc.reconnect.title', { name: c.label }), message: t(isShopifyCli(c) ? 'pc.reconnect.textShopifyCli' : 'pc.reconnect.text'),
      confirmLabel: t('pc.action.reconnect'),
    });
    if (!ok || !isOpen()) return;
    connect(c);
  }

  async function check(c) {
    if (busy.has(c.id)) return;
    busy.add(c.id);
    render();
    try {
      const saved = connectorOf(await ctx.api.post(`${LIBRARY_BASE}/${enc(c.id)}/check`, {}, { timeoutMs: CHECK_TIMEOUT_MS }));
      if (!isOpen()) return;
      if (saved) onSaved(saved);
      const after = saved || c;
      const kind = after.status === 'connected' ? 'success' : after.status === 'added' ? 'info' : 'warn';
      ctx.toast[kind](t(`pc.checked.${after.status}`, { name: after.label }));
    } catch (err) {
      if (isOpen()) ctx.toast.error(errorText(err));
    } finally {
      busy.delete(c.id);
      render();
    }
  }

  async function rename(c) {
    if (busy.has(c.id)) return;
    const value = await ctx.overlay.prompt({
      title: t('pc.rename.title', { name: c.label }), label: t('pc.rename.label'), hint: t('pc.rename.hint'),
      value: c.label, maxLength: LABEL_MAX, confirmLabel: t('pc.action.rename'),
    });
    if (value === null || value === undefined || !isOpen()) return;
    const checked = validateLabel(value);
    if (!checked.ok) { ctx.toast.error(checked.error); return; }
    if (checked.value === c.label) return;
    if (labelTaken(checked.value, list(), c.id)) { ctx.toast.error(t('pc.err.labelTaken', { label: checked.value })); return; }
    busy.add(c.id);
    render();
    try {
      const saved = connectorOf(await ctx.api.patch(`${LIBRARY_BASE}/${enc(c.id)}`, { label: checked.value }));
      if (!isOpen()) return;
      onSaved(saved || { ...c, label: checked.value });
      ctx.toast.success(t('pc.renamed'));
    } catch (err) {
      if (isOpen()) ctx.toast.error(errorText(err));
    } finally {
      busy.delete(c.id);
      render();
    }
  }

  async function remove(c) {
    if (busy.has(c.id)) return;
    const other = !c.mine && !!c.addedByName;
    // A Google Drive: its access is erased here only, its files stay in Drive (the server refuses
    // one storing or holding the office's files, with its reason).
    const drive = c.catalogId === GDRIVE_ID ? 'Drive' : '';
    const ok = await ctx.overlay.confirm({
      title: t('pc.remove.title', { name: c.label }),
      message: other ? t(`pc.remove.text${drive}Other`, { who: c.addedByName }) : t(`pc.remove.text${drive}`),
      confirmLabel: t('pc.action.remove'), danger: true,
    });
    if (!ok || !isOpen()) return;
    busy.add(c.id);
    render();
    try {
      await ctx.api.del(`${LIBRARY_BASE}/${enc(c.id)}`, { timeoutMs: WRITE_TIMEOUT_MS });
      running.delete(c.id);
      if (!isOpen()) return;
      onRemoved(c);
      ctx.toast.success(t('pc.removed', { name: c.label }));
    } catch (err) {
      if (isOpen()) ctx.toast.error(errorText(err));
    } finally {
      busy.delete(c.id);
      render();
    }
  }

  return { busy, running, connect, reconnect, settings, check, rename, remove, closeAll: () => children.closeAll('route') };
}
